import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { leadApi } from '@/services/api';
import { supabase } from '@/integrations/supabase/client';
import {
  ZameenContactHeader,
  ZameenContactActions,
  ZameenField,
  zameenInputClass,
  zameenTextareaClass,
} from '../ZameenContactParts';

interface MobileLeadFormSectionProps {
  listingId: string;
  listingLabel?: string;
  referenceCode?: string;
  listingKind?: 'property' | 'plot' | 'project';
  /** Optional full record so we can render the Zameen-style agency header + Call CTA. */
  property?: any;
}

export function MobileLeadFormSection({
  listingId,
  listingLabel = 'property',
  referenceCode,
  listingKind = 'property',
  property,
}: MobileLeadFormSectionProps) {
  const refId = (referenceCode || `ID${listingId.slice(0, 8)}`).toUpperCase();
  const defaultMsg = `I would like to inquire about your ${listingLabel} EthioRealEstates - ${refId}. Please contact me at your earliest convenience.`;
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: defaultMsg });
  const [role, setRole] = useState('buyer');
  const [keepInformed, setKeepInformed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Fetch owner's agency context for the header
  const [ownerInfo, setOwnerInfo] = useState<any>(null);
  useEffect(() => {
    const ownerId = property?.user_id;
    if (!ownerId) return;
    (async () => {
      const { data: agent } = await supabase
        .from('agent_profiles')
        .select('photo_url, phone_number, agency_id, agencies(name, logo_url, phone, slug)')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (agent) { setOwnerInfo({ kind: 'agent', ...agent }); return; }
      const { data: dev } = await supabase
        .from('developer_profiles')
        .select('logo_url, phone, company_name, slug')
        .eq('user_id', ownerId)
        .maybeSingle();
      if (dev) setOwnerInfo({ kind: 'developer', ...dev });
    })();
  }, [property?.user_id]);

  const agency = ownerInfo?.agencies;
  const contactPhone =
    property?.contact_phone ||
    ownerInfo?.phone_number ||
    ownerInfo?.phone ||
    agency?.phone ||
    '+251911000000';
  const agentName = property?.contact_person || 'Property Agent';
  const agencyName =
    agency?.name ||
    ownerInfo?.company_name ||
    property?.agency_name ||
    property?.developer_name ||
    agentName;
  const displayLogo =
    agency?.logo_url || ownerInfo?.logo_url || property?.agency_logo_url || property?.logo_url || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await leadApi.create({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        message: form.message.trim() || undefined,
        ...(listingKind === 'plot'
          ? { plot_id: listingId }
          : listingKind === 'project'
          ? { project_id: listingId }
          : { property_id: listingId }),
        source: 'website',
      });
      toast.success('Your inquiry has been sent!');
      setForm({ name: '', phone: '', email: '', message: defaultMsg });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send inquiry. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <section id="lead-form" className="scroll-mt-[120px]">
      <Card className="border-2 border-primary/20">
        <CardContent className="p-4 sm:p-5">
          <ZameenContactHeader
            logoUrl={displayLogo}
            agencyName={agencyName}
            agentName={agentName}
            refId={refId}
          />

          <ZameenContactActions phone={contactPhone} />

          <form onSubmit={handleSubmit} className="space-y-3">
            <ZameenField label="Name" required htmlFor="mlf-name">
              <input
                id="mlf-name"
                required
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Email" htmlFor="mlf-email">
              <input
                id="mlf-email"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className={zameenInputClass}
              />
            </ZameenField>

            <ZameenField label="Phone" required htmlFor="mlf-phone">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">🇪🇹 +251</span>
                <input
                  id="mlf-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className={zameenInputClass}
                />
              </div>
            </ZameenField>

            <ZameenField label="Message" required htmlFor="mlf-message">
              <textarea
                id="mlf-message"
                rows={3}
                required
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className={zameenTextareaClass}
              />
            </ZameenField>

            <div className="space-y-2 pt-1">
              <Label className="text-xs font-medium">I am a:</Label>
              <RadioGroup value={role} onValueChange={setRole} className="flex gap-4 flex-wrap">
                {[
                  { value: 'buyer', label: 'Buyer / Tenant' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'other', label: 'Other' },
                ].map(opt => (
                  <div key={opt.value} className="flex items-center gap-1.5">
                    <RadioGroupItem value={opt.value} id={`mlf-role-${opt.value}`} />
                    <Label htmlFor={`mlf-role-${opt.value}`} className="text-xs cursor-pointer">
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="mlf-keep-informed"
                checked={keepInformed}
                onCheckedChange={c => setKeepInformed(!!c)}
              />
              <Label htmlFor="mlf-keep-informed" className="text-xs cursor-pointer">
                Keep me informed about similar properties.
              </Label>
            </div>

            <Button type="submit" className="w-full h-11 font-bold gap-2" disabled={submitting}>
              <Send className="h-4 w-4" />
              {submitting ? 'Sending...' : 'Send Message'}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">
              By submitting this form, you agree to our{' '}
              <span className="text-primary cursor-pointer">Terms of Use</span>.
            </p>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
